import { useState } from 'react';
import axios from 'axios';
import { X, ChevronDown, Check } from 'lucide-react';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { ziyadahOptions, ZIYADAH_HINT, halamanToInput } from '@/utils/halaman';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const JUZ_LIST = Array.from({ length: 30 }, (_, i) => i + 1);
const ZIYADAH_OPTS = ziyadahOptions(10);

const JENIS_OPTIONS = [
  { value: 'ziyadah', label: 'Ziyadah' },
  { value: 'murajaah', label: 'Murajaah' },
  { value: 'bin_nadhar', label: 'Bin Nadzar' },
  { value: 'tasmi', label: "Tasmi'" },
  { value: 'iqro', label: 'Iqro' },
];

export default function EditSetoranModal({ setoran, onClose, onSaved }) {
  const [loading, setLoading] = useState(false);
  const [tanggal, setTanggal] = useState(setoran.tanggal || '');
  const [jenis, setJenis] = useState(setoran.jenis || 'murajaah');
  const [juz, setJuz] = useState(setoran.juz || 1);
  const [dariHalaman, setDariHalaman] = useState(setoran.dari_halaman ?? '');
  const [sampaiHalaman, setSampaiHalaman] = useState(setoran.sampai_halaman ?? '');
  // untuk ziyadah pakai angka (stepped), lainnya string editable
  const [jmlZiyadah, setJmlZiyadah] = useState(Number(setoran.jml_halaman) || 1);
  const [jmlRange, setJmlRange] = useState(halamanToInput(setoran.jml_halaman));
  const [iqroJilid, setIqroJilid] = useState(setoran.iqro_jilid || 1);
  const [iqroHalaman, setIqroHalaman] = useState(setoran.iqro_halaman || 1);
  const [jumlahJuz, setJumlahJuz] = useState(setoran.jumlah_juz || 1);
  const [tasmiStatus, setTasmiStatus] = useState(setoran.tasmi_status || 'Lulus');
  const [catatan, setCatatan] = useState(setoran.catatan || '');

  const isIqro = jenis === 'iqro';
  const isTasmi = jenis === 'tasmi';
  const isZiyadah = jenis === 'ziyadah';
  const showRange = !isIqro && !isTasmi;

  const recomputeJmlRange = (d, s) => {
    const dn = parseInt(d), sn = parseInt(s);
    if (!isNaN(dn) && !isNaN(sn) && sn >= dn) setJmlRange(String(sn - dn + 1));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    let halamanNum = 0;
    if (showRange) {
      halamanNum = isZiyadah ? Number(jmlZiyadah) : Number(jmlRange);
      if (!halamanNum || halamanNum <= 0) {
        toast.error(isZiyadah ? 'Pilih jumlah setoran' : 'Isi jumlah halaman lebih dari 0');
        return;
      }
    }
    setLoading(true);
    try {
      const payload = {
        santri_id: setoran.santri_id,
        tanggal,
        jenis,
        catatan,
        grade: '',
        ...(showRange ? {
          juz: parseInt(juz) || 1,
          dari_halaman: dariHalaman === '' ? null : parseInt(dariHalaman),
          sampai_halaman: sampaiHalaman === '' ? null : parseInt(sampaiHalaman),
          jml_halaman: halamanNum,
        } : {}),
        ...(isIqro ? { iqro_jilid: iqroJilid, iqro_halaman: iqroHalaman, jml_halaman: 0 } : {}),
        ...(isTasmi ? { jumlah_juz: jumlahJuz, tasmi_status: tasmiStatus, jml_halaman: 0 } : {}),
      };
      await axios.put(`${API}/setoran/${setoran.id}`, payload);
      toast.success('Setoran berhasil diperbarui');
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Gagal memperbarui setoran');
    } finally { setLoading(false); }
  };

  return (
    <AnimatePresence>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/40 z-50 flex items-end justify-center"
        onClick={e => e.target === e.currentTarget && !loading && onClose()}>
        <motion.div initial={{ y: 100 }} animate={{ y: 0 }} exit={{ y: 100 }}
          className="bg-white rounded-t-3xl w-full max-w-md flex flex-col"
          style={{ maxHeight: '92vh' }}
          data-testid="edit-setoran-modal">
          <div className="flex items-center justify-between p-5 border-b border-gray-100 flex-shrink-0">
            <div>
              <h2 className="font-playfair text-lg font-bold text-emerald-950">Edit Setoran</h2>
              <p className="text-xs text-gray-400">{setoran.tanggal} · {JENIS_OPTIONS.find(j => j.value === setoran.jenis)?.label || setoran.jenis}</p>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-xl"><X size={18} /></button>
          </div>

          <form onSubmit={handleSave} className="overflow-y-auto flex-1 p-4 space-y-4">
            {/* Tanggal & Jenis */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1.5">Tanggal</label>
                <input type="date" required value={tanggal} onChange={e => setTanggal(e.target.value)}
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20" />
              </div>
              <div>
                <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1.5">Jenis</label>
                <div className="relative">
                  <select value={jenis} onChange={e => setJenis(e.target.value)}
                    className="w-full pl-3 pr-6 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 appearance-none bg-white">
                    {JENIS_OPTIONS.map(j => <option key={j.value} value={j.value}>{j.label}</option>)}
                  </select>
                  <ChevronDown size={12} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                </div>
              </div>
            </div>

            {/* Juz + Halaman */}
            {showRange && (
              <div className="bg-gray-50 rounded-xl p-3 space-y-2.5">
                <div>
                  <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">Juz</label>
                  <div className="relative">
                    <select value={juz} onChange={e => setJuz(parseInt(e.target.value))}
                      data-testid="edit-juz"
                      className="w-full pl-3 pr-6 py-2 border border-gray-200 rounded-xl text-sm appearance-none bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500/20">
                      {JUZ_LIST.map(j => <option key={j} value={j}>Juz {j}</option>)}
                    </select>
                    <ChevronDown size={11} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2.5">
                  <div>
                    <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">Dari Halaman</label>
                    <input type="number" min={1} value={dariHalaman}
                      onChange={e => { setDariHalaman(e.target.value); recomputeJmlRange(e.target.value, sampaiHalaman); }}
                      data-testid="edit-dari" placeholder="0"
                      className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm text-center focus:outline-none focus:ring-2 focus:ring-emerald-500/20" />
                  </div>
                  <div>
                    <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">Sampai Halaman</label>
                    <input type="number" min={1} value={sampaiHalaman}
                      onChange={e => { setSampaiHalaman(e.target.value); recomputeJmlRange(dariHalaman, e.target.value); }}
                      data-testid="edit-sampai" placeholder="0"
                      className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm text-center focus:outline-none focus:ring-2 focus:ring-emerald-500/20" />
                  </div>
                </div>
                {isZiyadah ? (
                  <div>
                    <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">Jumlah Setoran</label>
                    <div className="relative">
                      <select value={jmlZiyadah} onChange={e => setJmlZiyadah(Number(e.target.value))}
                        data-testid="edit-jml"
                        className="w-full pl-3 pr-6 py-2 border border-gray-200 rounded-xl text-sm appearance-none bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500/20">
                        {ZIYADAH_OPTS.map(o => <option key={o.value} value={o.value}>{o.label} halaman</option>)}
                      </select>
                      <ChevronDown size={11} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                    </div>
                    <p className="text-[10px] text-gray-400 mt-1 leading-snug">{ZIYADAH_HINT}</p>
                  </div>
                ) : (
                  <div>
                    <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">Jumlah Halaman</label>
                    <input type="number" min={0} step="0.25" value={jmlRange}
                      onChange={e => setJmlRange(e.target.value)}
                      data-testid="edit-jml" placeholder="0"
                      className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm text-center focus:outline-none focus:ring-2 focus:ring-emerald-500/20" />
                  </div>
                )}
              </div>
            )}

            {/* Iqro */}
            {isIqro && (
              <div className="bg-gray-50 rounded-xl p-3">
                <p className="text-[10px] font-bold text-gray-500 uppercase mb-2">Iqro</p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[10px] text-gray-500 mb-1">Jilid</label>
                    <select value={iqroJilid} onChange={e => setIqroJilid(parseInt(e.target.value))}
                      className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none">
                      {[1,2,3,4,5,6].map(j => <option key={j} value={j}>Jilid {j}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-[10px] text-gray-500 mb-1">Halaman</label>
                    <input type="number" min={1} max={200} value={iqroHalaman} onChange={e => setIqroHalaman(parseInt(e.target.value) || 1)}
                      className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none" />
                  </div>
                </div>
              </div>
            )}

            {/* Tasmi' */}
            {isTasmi && (
              <div className="bg-gray-50 rounded-xl p-3">
                <p className="text-[10px] font-bold text-gray-500 uppercase mb-2">Tasmi'</p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[10px] text-gray-500 mb-1">Jumlah Juz</label>
                    <input type="number" min={1} max={30} value={jumlahJuz} onChange={e => setJumlahJuz(parseInt(e.target.value) || 1)}
                      className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none" />
                  </div>
                  <div>
                    <label className="block text-[10px] text-gray-500 mb-2">Status</label>
                    <div className="grid grid-cols-2 gap-1.5">
                      {['Lulus', 'Remedial'].map(s => (
                        <button key={s} type="button" onClick={() => setTasmiStatus(s)}
                          className={`py-2 rounded-xl text-xs font-bold border-2 transition-all ${tasmiStatus === s ? (s === 'Lulus' ? 'bg-emerald-500 text-white border-emerald-500' : 'bg-red-500 text-white border-red-500') : 'bg-white text-gray-400 border-gray-200'}`}>
                          {s}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Catatan */}
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">Catatan Guru</label>
              <textarea value={catatan} onChange={e => setCatatan(e.target.value)} rows={2}
                placeholder="Catatan opsional..."
                className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 resize-none" />
            </div>

            <button data-testid="edit-setoran-save-btn" type="submit" disabled={loading}
              className="w-full flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white py-4 rounded-2xl font-bold shadow-lg shadow-emerald-200 transition-all disabled:opacity-60">
              {loading
                ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                : <><Check size={18} /> Simpan Perubahan</>}
            </button>
          </form>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
